import React, { useState, useEffect } from 'react';
import { usePWAInstall } from '../../hooks/usePWAInstall';

const InstallPrompt = () => {
  const { isInstallable, isInstalled, installApp, dismissPrompt } = usePWAInstall();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if app is already in standalone mode
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone ||
                      document.referrer.includes('android-app://');
    
    setIsStandalone(standalone);
    
    // Check if user is on iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Only show prompt if:
    // 1. App is installable
    // 2. Not already installed/standalone
    // 3. User hasn't dismissed recently
    const shouldShowPrompt = isInstallable && !isStandalone && !isInstalled;
    
    const lastDismissed = localStorage.getItem('pwaPromptDismissed');
    const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    if (shouldShowPrompt && (!lastDismissed || parseInt(lastDismissed) < oneWeekAgo)) {
      // Show prompt after user has interacted with the site
      const handleUserInteraction = () => {
        setTimeout(() => {
          setShowPrompt(true);
        }, 2000);
        document.removeEventListener('click', handleUserInteraction);
        document.removeEventListener('scroll', handleUserInteraction);
      };
      
      document.addEventListener('click', handleUserInteraction);
      document.addEventListener('scroll', handleUserInteraction);
      
      return () => {
        document.removeEventListener('click', handleUserInteraction);
        document.removeEventListener('scroll', handleUserInteraction);
      };
    }
  }, [isInstallable, isInstalled]);

  const handleInstall = async () => {
    try {
      const installed = await installApp();
      if (installed) {
        setShowPrompt(false);
        // Track installation
        console.log('App installed successfully');
      }
    } catch (error) {
      console.error('Installation failed:', error);
    }
  };

  const handleDismiss = () => {
    dismissPrompt();
    setShowPrompt(false);
    // Don't show again for 30 days
    localStorage.setItem('pwaPromptDismissed', Date.now().toString());
  };

  if (!showPrompt || isStandalone) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center p-4 z-50 sm:items-center sm:p-6">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 transform transition-all duration-300 scale-100 opacity-100">
        <div className="text-center">
          {/* App Icon */}
          <div className="mx-auto w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Install Paymeo App
          </h3>
          
          <p className="text-sm text-gray-600 mb-6">
            Install the app for a faster, better experience with offline access.
          </p>

          {isIOS ? (
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800 font-medium mb-2">
                How to install on iOS:
              </p>
              <ol className="text-xs text-blue-700 list-decimal list-inside space-y-1 text-left">
                <li>Tap the Share button <span className="font-bold">(□→)</span></li>
                <li>Scroll down and tap "Add to Home Screen"</li>
                <li>Tap "Add" in the top right</li>
              </ol>
            </div>
          ) : (
            <div className="space-y-3">
              <button
                onClick={handleInstall}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Install App
              </button>
              
              <button
                onClick={handleDismiss}
                className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-xl font-medium hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Maybe Later
              </button>
            </div>
          )}
          
          {isIOS && (
            <button
              onClick={handleDismiss}
              className="w-full mt-3 text-gray-500 text-sm font-medium hover:text-gray-700 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;